/**
 * @docs: docs/core/architecture/SCOUT_GATEWAY_UPDATES.md
 *
 * The owner's view of their Scout Gateway's software, on My Devices: the
 * version it runs, whether an update waits, and what happened last. The SG
 * installs updates by itself at a safe moment (boat still, anchor watch off,
 * not recording); the skipper can Postpone or Install now on the SG itself.
 */
import { useQuery } from '@tanstack/react-query';
import { api, type GatewayReport } from '../../services/api';
import { formatRelativeTime } from '../../utils/formatters';

const CHANNEL: Record<string, string> = { test: 'Test', early: 'Early', stable: 'Stable' };

export function reportText(r: GatewayReport): string {
  const v = r.version ? `SG ${r.version}` : 'An update';
  switch (r.result) {
    case 'installed': return `${v} installed${r.detail === 'install now' ? ' (Install now)' : ' at a safe moment'}`;
    case 'kept': return `${v} kept after 10 healthy minutes`;
    case 'rolled_back': return `${v} rolled back by itself${r.detail ? `: ${r.detail}` : ''}`;
    case 'failed': return `${v} did not start; SG kept the version it had`;
  }
}

export function SgSoftwareCard({ deviceId }: { deviceId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['gateway-software', deviceId],
    queryFn: () => api.getGatewaySoftware(deviceId),
    staleTime: 60_000,
  });

  return (
    <div className="bg-slate-700/50 rounded-lg p-3" data-testid="sg-software">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-400">Software</p>
        {data && <span className="text-sm text-slate-400">{CHANNEL[data.channel]} updates</span>}
      </div>
      {isLoading && <p className="text-sm text-slate-400 mt-1">Checking…</p>}
      {isError && <p className="text-sm text-slate-400 mt-1">Software details are not available right now.</p>}
      {data && (
        <>
          <p className="text-white mt-1">{data.version ? `SG ${data.version}` : 'Version not reported yet'}</p>
          {data.update ? (
            <p className="text-sm text-amber-300 mt-1">
              SG {data.update.version} is ready. It installs by itself when the boat is still, with anchor watch off
              and nothing recording.{data.update.notes ? ` ${data.update.notes}` : ''}
            </p>
          ) : (
            data.version && <p className="text-sm text-green-400 mt-1">Up to date</p>
          )}
          {data.last && (
            <p className="text-sm text-slate-400 mt-2">
              Last: {reportText(data.last)} · {formatRelativeTime(data.last.at)}
            </p>
          )}
        </>
      )}
    </div>
  );
}
